import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import { singleSelectionLabel } from '../../utils/filterLabels';
import { registerFilterAnchor } from './filterAnchor';

const optionListIncludes = (options, v) =>
  options.some((o) => String(o) === String(v));

/**
 * Reusable dropdown filter component
 *
 * Simple mode: pass `value` (string) for the main label.
 * Multi-select summary mode: pass `options` + `selected` or `selectedValues` (arrays),
 * plus optional `allLabel`, `placeholder`, and `getOptionLabel`.
 *
 * @param {Object} props
 * @param {string} props.label - Label text above the dropdown
 * @param {string} [props.value] - Display value when not using multi summary props
 * @param {Array} [props.options] - Valid option values (multi summary mode)
 * @param {Array} [props.selected] - Selected values (multi summary mode)
 * @param {Array} [props.selectedValues] - Alias for `selected`
 * @param {string} [props.allLabel] - Shown when every option is selected
 * @param {string} [props.placeholder] - Shown when nothing selected (or empty state)
 * @param {Function} [props.getOptionLabel] - (option) => display string for short lists
 * @param {Function} props.onClick - Click handler to open modal/menu
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {number} [props.fontSize] - Optional font size override (e.g. chart context)
 */
const FilterDropdown = ({
  label,
  value,
  options,
  selected,
  selectedValues,
  allLabel,
  placeholder,
  getOptionLabel,
  onClick,
  disabled = false,
  className = '',
  fontSize: fontSizeProp,
  // When true, long values wrap onto multiple lines instead of being
  // truncated with an ellipsis. Callers using a layout that grows the
  // button (e.g. an auto-fit grid) should opt in so values like
  // "Flats 8 1-Lb Container" remain fully readable.
  wrapValue = false,
  // Compact chip form used by the Analytics preset filter strip: fixed 11px
  // label + 27px unfilled control, sized to its content so the whole strip
  // fits on one line. Ignores `fontSize` by design — the strip is a fixed
  // scale, not a body-text-relative one.
  compact = false,
}) => {
  const { t } = useTranslation();
  const { fontSize: globalFontSize } = useGlobalFontSize();
  const fontSize = fontSizeProp ?? globalFontSize;

  const selectionList = selected !== undefined && selected !== null ? selected : selectedValues;

  const useMultiSummary =
    Array.isArray(options) &&
    selectionList !== undefined &&
    selectionList !== null &&
    Array.isArray(selectionList);

  const handleClick = (e) => {
    registerFilterAnchor(e.currentTarget);
    onClick?.(e);
  };

  const fallback = placeholder ?? t('filters.selectPlaceholder', 'Select...');

  let displayText = fallback;
  if (useMultiSummary) {
    const opts = options;
    const sel = selectionList;

    // A lone selection always reads as itself — even when it is also the only
    // option, where "All X" would otherwise hide which value that is.
    const onlySelection = singleSelectionLabel(sel, opts, getOptionLabel);

    if (!Array.isArray(opts)) {
      displayText = fallback;
    } else if (opts.length === 0) {
      displayText = onlySelection ?? (sel.length > 0 ? t('filters.nSelected', { count: sel.length }) : fallback);
    } else if (sel.length === 0) {
      displayText = fallback;
    } else if (onlySelection) {
      displayText = onlySelection;
    } else {
      const active = sel.filter((v) => optionListIncludes(opts, v));
      const activeCount = active.length;

      if (activeCount === 0) {
        displayText = t('filters.nSelected', { count: sel.length });
      } else if (activeCount === opts.length) {
        displayText = allLabel ?? fallback;
      } else if (typeof getOptionLabel === 'function') {
        if (activeCount <= 3) displayText = active.map((x) => getOptionLabel(x)).join(', ');
        else displayText = t('filters.nSelected', { count: activeCount });
      } else if (activeCount <= 3) {
        displayText = active.map(String).join(', ');
      } else {
        displayText = t('filters.nSelected', { count: activeCount });
      }
    }
  } else if (value === 'N/A') {
    displayText = 'Not Classified';
  } else if (value != null && String(value).trim() !== '') {
    displayText = String(value);
  } else {
    displayText = fallback;
  }

  if (compact) {
    return (
      <div className={`min-w-0 lg:shrink-0 ${className}`}>
        {label && (
          <label className="mb-[4px] block text-[11px] font-medium text-[#828382] dark:text-gray-400">
            {label}
          </label>
        )}
        {/* Below lg the chip fills its grid column so the row spans the full
            width; the value stays on one line and truncates with an ellipsis
            rather than wrapping, which would make the chip taller than its
            row. From lg up it goes back to the content-sized chip that the
            scrollable strip expects. */}
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          title={displayText}
          className={`flex h-[27px] w-full items-center justify-between rounded-[8px] border border-[#E2E2E2] px-[10px] text-left text-[11px] text-[#282A29] transition-colors lg:w-auto lg:min-w-[76px] lg:max-w-[320px] dark:border-[#3a3c3b] dark:text-white ${
            disabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:border-bloom-green dark:hover:border-green-500/50'
          }`}
        >
          <span className="truncate">{displayText}</span>
          <ChevronDown
            size={14}
            className="ml-[6px] shrink-0 text-[#828382] dark:text-gray-400"
          />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex-1 min-w-0 ${className}`}>
      {label && (
        <label
          className="block text-[#828382] dark:text-gray-400 mb-1 font-medium"
          style={{ fontSize: fontSize - 2 }}
        >
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={displayText}
        className={`
          w-full flex items-center justify-between
          bg-white dark:bg-[#282A29] border border-[#E8EBE9] dark:border-[#3a3c3b] rounded-lg
          px-3 py-2.5 text-left
          transition-colors duration-200
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:border-bloom-green dark:hover:border-green-500/50 cursor-pointer'
          }
        `}
      >
        <span
          className={`text-[#282A29] dark:text-white flex-1 mr-2 ${
            wrapValue ? 'whitespace-normal break-words leading-tight' : 'truncate'
          }`}
          style={{ fontSize }}
        >
          {displayText}
        </span>
        <ChevronDown size={16} className="text-[#828382] dark:text-gray-400 flex-shrink-0" />
      </button>
    </div>
  );
};

export default FilterDropdown;
